/**
 * Guest Exit Prevention Context
 * 
 * Shares guest chat session state between components (TaxOracleChatArea, Sidebar)
 * to enable coordinated exit prevention and auth redirects.
 */

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSmartAuthRoute } from '@/utils/authRedirect';
import { ROUTES } from '@/constants';

interface GuestExitPreventionContextValue {
  /** Whether a guest has an active chat session with messages */
  hasActiveGuestSession: boolean;
  /** Current session ID for the guest */
  sessionId: string | null;
  /** Register an active guest session */
  registerSession: (sessionId: string) => void;
  /** Clear the active session */
  clearSession: () => void;
  /** Whether the exit modal is open */
  showExitModal: boolean;
  /** Open the exit prevention modal */
  openExitModal: () => void;
  /** Close the exit prevention modal */
  closeExitModal: () => void;
  /** Store session and navigate to auth */
  handleAuthRedirect: (tab?: 'signin' | 'signup') => void;
  /** Pending navigation path */
  pendingPath: string | null;
  /** Set pending path for deferred navigation */
  setPendingPath: (path: string | null) => void;
  /** Proceed with pending navigation */
  proceedNavigation: () => void;
  /** Proceed with back navigation (browser back button) */
  proceedBackNavigation: () => void;
  /** Request navigation - shows modal if guest has active session on /chat */
  requestNavigation: (path: string) => boolean;
  /** Whether this is a back button navigation (no pendingPath) */
  isBackNavigation: boolean;
}

const GuestExitPreventionContext = createContext<GuestExitPreventionContextValue | null>(null);

interface GuestExitPreventionProviderProps {
  children: ReactNode;
}

export function GuestExitPreventionProvider({ children }: GuestExitPreventionProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [isBackNavigation, setIsBackNavigation] = useState(false);

  const hasActiveGuestSession = !!sessionId;
  const isOnChatRoute = location.pathname.startsWith('/chat');
  const shouldPreventExit = hasActiveGuestSession && isOnChatRoute;

  const registerSession = useCallback((id: string) => {
    setSessionId(id);
  }, []);

  const clearSession = useCallback(() => {
    setSessionId(null);
  }, []);

  const openExitModal = useCallback(() => {
    setShowExitModal(true);
  }, []);

  const closeExitModal = useCallback(() => {
    setShowExitModal(false);
    setPendingPath(null);
    setIsBackNavigation(false);
  }, []);

  const handleAuthRedirect = useCallback((tab?: 'signin' | 'signup') => {
    // Store session ID for claiming after auth
    if (sessionId) {
      sessionStorage.setItem('pendingGuestSessionId', sessionId);
    }
    
    setShowExitModal(false);
    // Use smart detection if no explicit tab, otherwise honor the explicit choice
    const authPath = tab 
      ? (tab === 'signup' ? ROUTES.AUTH_SIGNUP : ROUTES.AUTH)
      : getSmartAuthRoute();
    navigate(authPath);
  }, [sessionId, navigate]);

  const proceedNavigation = useCallback(() => {
    setShowExitModal(false);
    if (pendingPath) {
      navigate(pendingPath);
      setPendingPath(null);
    }
    setIsBackNavigation(false);
  }, [pendingPath, navigate]);

  const proceedBackNavigation = useCallback(() => {
    setShowExitModal(false);
    setIsBackNavigation(false);
    // Go back twice: once for dummy state we pushed, once for actual navigation
    window.history.go(-2);
  }, []);

  /**
   * Request navigation to a path - intercepts if guest has active session on /chat
   * Returns true if navigation was intercepted (modal shown), false if should proceed normally
   */
  const requestNavigation = useCallback((path: string): boolean => {
    // Only intercept if guest has active session AND currently on chat route
    if (shouldPreventExit) {
      setPendingPath(path);
      setIsBackNavigation(false);
      setShowExitModal(true);
      return true; // Navigation was intercepted
    }
    
    return false; // Navigation should proceed normally
  }, [shouldPreventExit]);

  // Intercept browser back/forward buttons
  useEffect(() => {
    if (!shouldPreventExit) return;
    
    // Push a dummy state to detect back button press
    window.history.pushState({ guestExitPrevention: true }, '', window.location.href);
    
    const handlePopState = () => {
      // Push state again to prevent navigation
      window.history.pushState({ guestExitPrevention: true }, '', window.location.href);
      // Show our modal for back navigation
      setIsBackNavigation(true);
      setPendingPath(null);
      setShowExitModal(true);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [shouldPreventExit]);

  // Intercept tab close and page refresh
  useEffect(() => {
    if (!shouldPreventExit) return;
    
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // Chrome requires this
      return ''; // Some browsers need return value
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldPreventExit]);

  return (
    <GuestExitPreventionContext.Provider
      value={{
        hasActiveGuestSession,
        sessionId,
        registerSession,
        clearSession,
        showExitModal,
        openExitModal,
        closeExitModal,
        handleAuthRedirect,
        pendingPath,
        setPendingPath,
        proceedNavigation,
        proceedBackNavigation,
        requestNavigation,
        isBackNavigation,
      }}
    >
      {children}
    </GuestExitPreventionContext.Provider>
  );
}

export function useGuestExitPrevention() {
  const context = useContext(GuestExitPreventionContext);
  if (!context) {
    throw new Error('useGuestExitPrevention must be used within GuestExitPreventionProvider');
  }
  return context;
}

// Optional hook that returns null if not in context (for conditional usage)
export function useGuestExitPreventionOptional() {
  return useContext(GuestExitPreventionContext);
}
